// General-purpose «ذخیره‌ها» client state — a tiny module-level store + a React
// hook. Mirrors market/wishlist-state.ts: a SINGLE in-memory set of saved keys
// backs every <BookmarkButton/> across the SPA (chat messages, library, labs,
// lessons, links). The set key is "<resourceType>:<resourceId>" (the same string
// GET /v1/bookmarks/ids returns), so the FE renders saved-state on every button
// WITHOUT an N+1 of per-resource calls.
//
// Behaviour (per the pinned contract + the wishlist analog): OPTIMISTIC toggle
// (flip the set immediately) + best-effort server call; on error, REVERT and
// rethrow so the caller can surface a message. A guest (no session) gets an empty
// set + a `toggle` that rejects (the caller routes to /login instead).

import React from "react";

import { bookmarkApi } from "../api/endpoints.js";

// ---------------------------------------------------------------------------
// Module-level store (shared across every mounted consumer in the SPA session).
// ---------------------------------------------------------------------------
let ids = new Set<string>();
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const l of listeners) l();
};

/** The set key for a resource — MUST match the server's `/ids` payload shape. */
export const bookmarkKey = (resourceType: string, resourceId: string): string =>
  `${resourceType}:${resourceId}`;

/** Fetch the caller's saved-id set once (deduped). Best-effort: a failure leaves it empty. */
const fetchOnce = async (): Promise<void> => {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = (await bookmarkApi.ids()) as { ids: string[] };
      ids = new Set(res.ids ?? []);
      loaded = true;
      emit();
    } catch {
      loaded = true; // mark ready so the UI stops waiting; the set stays as-is
      emit();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

/** Force a re-read (used after the bookmarks page removes a row, to reconcile buttons). */
export const refreshBookmarks = async (): Promise<void> => {
  inflight = null;
  await fetchOnce();
};

/** Imperatively reflect a known saved-state (used by the bookmarks page on remove). */
export const setBookmarkSaved = (
  resourceType: string,
  resourceId: string,
  saved: boolean,
): void => {
  const key = bookmarkKey(resourceType, resourceId);
  const next = new Set(ids);
  if (saved) next.add(key);
  else next.delete(key);
  ids = next;
  emit();
};

// ---------------------------------------------------------------------------
// The snapshot a button needs to save (a display title + where it navigates).
// ---------------------------------------------------------------------------
export interface BookmarkTarget {
  resourceType: string;
  resourceId: string;
  title?: string;
  targetHref?: string;
  note?: string;
}

// ---------------------------------------------------------------------------
// Hook — the shared saved-set + an optimistic toggle.
// ---------------------------------------------------------------------------
export interface UseBookmarks {
  /** true once the first fetch has resolved (or failed). */
  ready: boolean;
  /** the set of saved keys ("<type>:<id>"). */
  savedKeys: ReadonlySet<string>;
  isSaved: (resourceType: string, resourceId: string) => boolean;
  /**
   * Optimistically flip the saved state for a resource, call the API, then keep
   * the result. Returns the NEW saved state (true=added). On error it reverts and
   * rethrows so the caller can surface a message.
   */
  toggle: (target: BookmarkTarget) => Promise<boolean>;
}

/**
 * @param enabled  pass false for a guest (no session) — the hook then never fetches
 *                 and `toggle` rejects (the caller should route to /login instead).
 */
export const useBookmarks = (enabled: boolean = true): UseBookmarks => {
  const [, force] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (!enabled) return;
    const l = () => force();
    listeners.add(l);
    if (!loaded) void fetchOnce();
    return () => {
      listeners.delete(l);
    };
  }, [enabled]);

  const isSaved = React.useCallback(
    (resourceType: string, resourceId: string) => ids.has(bookmarkKey(resourceType, resourceId)),
    [],
  );

  const toggle = React.useCallback(
    async (target: BookmarkTarget): Promise<boolean> => {
      if (!enabled) throw new Error("not-authenticated");
      const key = bookmarkKey(target.resourceType, target.resourceId);
      const wasSaved = ids.has(key);
      // optimistic flip
      const next = new Set(ids);
      if (wasSaved) next.delete(key);
      else next.add(key);
      ids = next;
      emit();
      try {
        if (wasSaved) await bookmarkApi.remove(target.resourceType, target.resourceId);
        else
          await bookmarkApi.add({
            resourceType: target.resourceType,
            resourceId: target.resourceId,
            title: target.title,
            targetHref: target.targetHref,
            note: target.note,
          });
        return !wasSaved;
      } catch (err) {
        // revert on failure
        const reverted = new Set(ids);
        if (wasSaved) reverted.add(key);
        else reverted.delete(key);
        ids = reverted;
        emit();
        throw err;
      }
    },
    [enabled],
  );

  return { ready: loaded, savedKeys: ids, isSaved, toggle };
};
